import * as React from 'react'
import { FormikHelpers } from 'formik'

import { Box, Button, Space } from '@ally/metronome-ui'
import { AccountNumber, Form, Forms, VINHullId } from '@ally/forms'
import { AsyncStatus } from '@ally/transmitigator'

import {
  FindAccountNumberModal,
  FindVINModal,
  ForgotUsernameModal,
  WhyTaxIDModal,
} from '../../../Modals'
import {
  CIAMErrorBanner,
  InputWrapper,
  LastFourTaxID,
  StyledTextBody,
} from '../../Shared'
import { useAppState } from '../../../AppStateProvider'
import { useHostServices } from '../../../HostServices'
import { useTransmit } from '../../../TransmitContext'

const FORM_ID = 'ally-login-forgot-username-business-account-form'

interface BusinessAccountFormInputs {
  accountNumber: string
  tin: string // Only last 4 of tin required
  vin: string
}

interface BusinessAccountFormProps {
  error?: string
  status: AsyncStatus | 'Blocked' | 'Error'
  toggle: () => void
}

export const BusinessAccountForm: React.FC<BusinessAccountFormProps> = ({
  error,
  status,
  toggle,
}) => {
  const { isWebWrapped } = useAppState()
  const { featureFlags } = useHostServices()
  const { actions } = useTransmit()
  const initialValues = {
    accountNumber: '',
    tin: '',
    vin: '',
  }

  const onSubmit = (
    { accountNumber, tin, vin }: BusinessAccountFormInputs,
    { setSubmitting }: FormikHelpers<BusinessAccountFormInputs>,
  ): void => {
    actions.forgotUsername({
      accountNumber,
      tin,
      vin,
      isAutoBusinessUser: true,
    })

    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  return (
    <>
      <Space mb="md">
        <StyledTextBody size="sm" tag="p">
          To find your username, we need some info about your auto account. If
          you need any help,{' '}
          <ForgotUsernameModal
            buttonText="just give us a call"
            withAllyTM={
              isWebWrapped
                ? 'ally mobile app:Web View:Forgot Username:TODO handle with analytics story'
                : 'forgotUsername-giveUsACall'
            }
            variation={featureFlags.variation}
          />
          .
        </StyledTextBody>
      </Space>

      <Space mb="md">
        <Button
          withAllyTM="forgotUsername-dontHaveAnAutoBusinessAccount"
          onClick={toggle}
          text="Don’t have an auto business account?"
          variant="link"
        />
      </Space>

      <CIAMErrorBanner mb="md" error={error} />

      <Forms formId={FORM_ID} initialValues={initialValues} onSubmit={onSubmit}>
        <Form id={FORM_ID}>
          <InputWrapper mb="md">
            <LastFourTaxID name="tin" />

            <Box mt="sm" mb="xl">
              <WhyTaxIDModal allytmln="forgotUsername-whyDoINeedMyTaxID" />
            </Box>
          </InputWrapper>
          <InputWrapper mb="md">
            <AccountNumber name="accountNumber" />

            <Box mt="sm" mb="xl">
              <FindAccountNumberModal allytmln="forgotUsername-whereCanIFindMyAccountNumber" />
            </Box>
          </InputWrapper>
          <InputWrapper mb="md">
            <VINHullId name="vin" />

            <Box mt="sm" mb="xl">
              <FindVINModal allytmln="forgotUsername-whereCanIFindMyVIN" />
            </Box>
          </InputWrapper>

          <Button
            withAllyTM="forgotUsername-accountInfo-continue"
            type="submit"
            variant="primary"
            text="Continue"
            loading={status === 'Loading'}
          />
        </Form>
      </Forms>
    </>
  )
}
